import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import Foco from 'react-foco';
import { usePopper } from 'react-popper';
import { PopoverArrowStyled, PopoverContainerStyled, PopoverStyled, BoxStyled, } from './popover.styles';
export function usePopover() {
    const [popoverReference, setPopoverReference] = useState(null);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const closePopover = () => {
        setPopoverVisible(false);
    };
    const openPopover = () => {
        setPopoverVisible(true);
    };
    const togglePopover = () => {
        setPopoverVisible(!popoverVisible);
    };
    const PopoverContainer = useCallback(({ children, permanent = false, block, relativePosition, }) => (_jsx(Foco, { onClickOutside: !permanent && closePopover, children: _jsx(PopoverContainerStyled, { ref: setPopoverReference, block: block, relativePosition: relativePosition, children: children }) })), [setPopoverReference]);
    return {
        PopoverContainer,
        closePopover,
        openPopover,
        togglePopover,
        triggerProps: {
            'aria-haspopup': true,
        },
        popoverProps: {
            popoverReference,
            popoverVisible,
            closePopover,
        },
    };
}
/**
 * `<Popover>` takes a `placement` prop to determine its position in relation to `<PopoverContainer>`.
 *
 * We expose two exports:
 *
 * - `<Popover>`: contains the popover content
 * - `usePopover()`: a hook that returns methods and props to be passed to `<Popover>`, and the "trigger" element used to open it.
 * ```
 *   const {
 *    PopoverContainer,
 *    togglePopover,
 *    popoverProps,
 *    triggerProps
 *   } = usePopover()
 * ```
 *
 * `<Popover>` closes by default when you click outside `<PopoverContainer>`. Add the `permanent` prop to `PopoverContainer` to prevent this.
 *
 */
export const Popover = ({ children, placement, offset, popoverReference, popoverVisible, boxConfig = {}, closePopover, disableFlip, popoverWidth, boundaryElement = null, enableHitarea = false, onMouseOver, onMouseLeave, }) => {
    const [arrowElement, setArrowElement] = useState(null);
    const [popoverTarget, setPopoverTarget] = useState(null);
    const { shadow = 16, padding = 24, radius = 8 } = boxConfig;
    const offsetModifier = useMemo(() => {
        return {
            name: 'offset',
            options: {
                offset: ({ placement }) => {
                    // Map offset values to skidding and distance based on placement
                    const skidding = placement?.startsWith('top') || placement?.startsWith('bottom')
                        ? offset?.left || 0 // Horizontal adjustment
                        : offset?.top || 0; // Vertical adjustment
                    const distance = placement?.startsWith('top') || placement?.startsWith('bottom')
                        ? offset?.top || Math.round(padding / 1.5) // Vertical adjustment
                        : offset?.left || Math.round(padding / 1.5); // Horizontal adjustment
                    return [skidding, distance];
                },
            },
        };
    }, [placement, offset, padding]); // Dependencies for memoization
    const paddingFunction = useCallback(({ placement: popPlacement, popper }) => {
        if (['left-start', 'right-start'].includes(popPlacement)) {
            return {
                bottom: popper.height - 50,
            };
        }
        if (['left-end', 'right-end'].includes(popPlacement)) {
            return {
                top: popper.height - 50,
            };
        }
        if (['top-start', 'bottom-start'].includes(popPlacement)) {
            return {
                right: popper.width - 50,
            };
        }
        if (['top-end', 'bottom-end'].includes(popPlacement)) {
            return {
                left: popper.width - 50,
            };
        }
        return {};
    }, []);
    const { styles, attributes } = usePopper(popoverReference, popoverTarget, {
        placement,
        modifiers: [
            {
                name: 'arrow',
                options: {
                    element: arrowElement,
                    padding: paddingFunction,
                },
            },
            offsetModifier,
            {
                name: 'preventOverflow',
                options: {
                    boundary: boundaryElement,
                    padding: 0,
                },
            },
            disableFlip
                ? {
                    name: 'flip',
                    enabled: false,
                }
                : {
                    name: 'flip',
                    options: {
                        fallbackPlacements: [],
                    },
                },
        ],
    });
    const popperPlacement = attributes &&
        attributes.popper &&
        attributes.popper['data-popper-placement'];
    return popoverVisible ? (_jsxs(PopoverStyled, { className: "popoverStyled", "data-testid": "popover", ref: setPopoverTarget, style: styles.popper, radius: radius, ...attributes.popper, tabIndex: 0, onBlur: (event) => {
            // Prevent onBlur Popover close if target is inside the popover
            if (event.currentTarget.contains(event?.target)) {
                return;
            }
            if (!event.currentTarget.contains(event.relatedTarget)) {
                closePopover();
            }
        }, popoverWidth: popoverWidth, onMouseEnter: enableHitarea ? onMouseOver : undefined, onMouseLeave: enableHitarea ? onMouseLeave : undefined, children: [_jsx(BoxStyled, { shadow: shadow, colourScheme: "white", padding: [padding, padding, padding], radius: radius, boxWidth: popoverWidth, enableHitarea: enableHitarea, placement: placement, offset: offset, children: children }), popperPlacement && (_jsx(PopoverArrowStyled, { className: "popoverArrowStyled", placement: popperPlacement, ref: setArrowElement, style: styles.arrow }))] })) : null;
};
//# sourceMappingURL=popover.js.map